import type { ConversationListItemFragment } from '@gql'
import { MessagingMessageDirection } from '@gql'
import {
  getLastReadSeq,
  getUnreadConversationCount,
  getUnreadVersion,
  subscribeUnread,
} from '@sections/messaging/store/unreadStore'
import { useCallback, useSyncExternalStore } from 'react'

/** Re-render subscriber for the unread store. */
function useUnreadVersion(): number {
  return useSyncExternalStore(subscribeUnread, getUnreadVersion, () => 0)
}

/**
 * The seq of the newest INBOUND message, or null when the thread has none.
 *
 * `lastInboundSeq` is the server's answer (crm-core, schemas 0.119.0). It is
 * NOT `lastMessage.seq`-when-inbound: an office-hours auto-reply, a bot turn or
 * a covering lead's send lands AFTER the customer's message and flips the
 * preview to outbound while that message is still unseen — which is how an
 * out-of-hours lead used to sit in the inbox with no dot. The preview is only
 * the fallback for a row that predates the field (a cached list from before
 * the deploy), where an inbound preview is the one thing still known.
 */
export function getLastInboundSeq(
  conversation: Pick<ConversationListItemFragment, 'lastInboundSeq' | 'lastMessage'>,
): number | null {
  if (conversation.lastInboundSeq !== undefined) {
    return conversation.lastInboundSeq
  }

  const last = conversation.lastMessage
  return last && last.direction === MessagingMessageDirection.IN ? last.seq : null
}

/** A conversation is unread when its newest INBOUND message is newer than the
 * last seq the operator has seen — regardless of what was sent after it. */
export function isConversationUnread(conversation: ConversationListItemFragment): boolean {
  const lastInboundSeq = getLastInboundSeq(conversation)
  if (lastInboundSeq === null) {
    return false
  }

  return lastInboundSeq > getLastReadSeq(conversation.id)
}

/** Returns a predicate whose IDENTITY changes whenever the seen-map does, so
 * callers may use it as a memo dependency.
 *
 * The version must stay in the dep array. With `[]` the identity was frozen
 * for the lifetime of the hook: `markConversationRead` bumped the store, every
 * subscriber re-rendered — and `useConversationList`'s `allViews` memo saw
 * `items`, `isUnread`, `intl` and `now` all unchanged, so it handed back the
 * cached views carrying the OLD unread flags, and `counts.unread` plus the nav
 * badge inherited the staleness until something else moved (up to the 30s
 * `useNow` tick). It looked correct in practice only because opening a thread
 * usually also patches the row, producing a new `items` reference — an
 * accidental coupling between two unrelated mechanisms. */
export function useIsConversationUnread(): (conversation: ConversationListItemFragment) => boolean {
  // Subscribe so consumers re-render on read-state changes.
  const version = useUnreadVersion()
  return useCallback(
    (conversation: ConversationListItemFragment) => {
      // Deliberately load-bearing REFERENCE, not just a dep-array entry:
      // isConversationUnread reads getLastReadSeq() out of the external
      // store, so the closure's RESULT changes without its inputs changing.
      // Referencing `version` in the body keeps the dependency real for the
      // React Compiler (which infers deps from the body and may drop a
      // dep-array-only entry); re-minting the identity per version is what
      // invalidates callers' memos — without it the unread flags freeze.
      void version
      return isConversationUnread(conversation)
    },
    [version],
  )
}

/** Live count for the nav badge (conversations with unread).
 *
 * The count itself is the store snapshot — NOT `useUnreadVersion()` + a bare
 * `getUnreadConversationCount()` read. The React Compiler (browser build
 * only; vitest runs uncompiled) memoizes that bare call as pure, so the
 * version bump re-rendered the component but the cached count never moved. */
export function useUnreadConversationCount(): number {
  return useSyncExternalStore(subscribeUnread, getUnreadConversationCount, () => 0)
}
